/**
 * Olloport additions to SuperSplat, wired in one place.
 *
 * Everything under src/ollo/ is ours; the upstream tree carries only the
 * hooks listed in documentation/viewer/PLAYCANVAS_UPGRADE.md. Register order
 * matters: doc-meta needs `doc.saveToStream` (doc.ts), the scene elements need
 * the scene, and the backend bootstrap (`?scene=<id>`) runs last, after the
 * `?load=` files, from main.ts via `ollo.init`.
 */

import { Asset } from 'playcanvas';

import type { Events } from '../events';
import type { ImportFile } from '../file-handler';
import type { Scene } from '../scene';
import type { ToolManager } from '../tools/tool-manager';
import type { EditorUI } from '../ui/editor';
import { initOlloport, openSceneFromBackend, saveToOlloport } from './backend';
import { registerDocMeta } from './doc-meta';
import { startHelperMonitor } from './lod-helper-client';
import { Markers } from './markers';
import { Model } from './model';
import { OrbitBounds } from './orbit-bounds';
import { registerSceneExportEvents } from './scene-export';
import { registerLighting } from './lighting';
import { registerStartView } from './start-view';
import { AlignTool } from './tools/align-tool';
import { MarkersTool } from './tools/markers-tool';
import { WalkTool } from './tools/walk-tool';
import { AlignPanel } from './ui/align-panel';
import { LoginDialog } from './ui/login-dialog';
import { MarkersPanel } from './ui/markers-panel';
import { OlloportPanel } from './ui/olloport-panel';
import { ViewerPreview } from './ui/viewer-preview';
import { WalkPanel } from './ui/walk-panel';
import { registerViewerSettings } from './viewer-settings';
import { WalkArea } from './walk-area';
import { WalkPreview } from './walk-preview';
import { WalkVoxels } from './walk-voxels';

/** Load a GLB/glTF through the engine's ContainerHandler and add it as a Model element. */
const importModel = async (scene: Scene, file: ImportFile): Promise<Model> => {
    let url: string;
    let revoke: (() => void) | null = null;
    if (file.contents) {
        const blob = file.contents instanceof Blob ?
            file.contents :
            new Blob([file.contents as unknown as ArrayBuffer], { type: 'model/gltf-binary' });
        url = URL.createObjectURL(blob);
        revoke = () => URL.revokeObjectURL(url);
    } else if (file.url) {
        url = file.url;
    } else {
        throw new Error('Model import requires either contents or url');
    }
    try {
        const asset = new Asset(file.filename, 'container', { url, filename: file.filename });
        scene.app.assets.add(asset);
        await new Promise<void>((resolve, reject) => {
            asset.once('load', () => resolve());
            asset.once('error', (err: unknown) => reject(new Error(`GLB load failed: ${(err as Error)?.message ?? err}`)));
            scene.app.assets.load(asset);
        });
        if (!asset.resource) throw new Error('the engine returned no resource for this model');
        const model = new Model(asset);
        await scene.add(model);
        return model;
    } finally {
        revoke?.();
    }
};

const registerOlloport = async (events: Events, scene: Scene, editorUI: EditorUI, toolManager: ToolManager) => {
    events.function('ollo.scene', () => scene);
    // status line under the spinner (LOD build stage, upload progress …)
    events.on('spinner.text', (text: string) => {
        events.fire('spinner.setText', text ?? '');
    });

    registerViewerSettings(events);
    registerStartView(events);
    registerDocMeta(events, scene);
    registerSceneExportEvents(scene, events);
    registerLighting(events, scene);

    events.function('ollo.importModel', (file: ImportFile) => importModel(scene, file));

    // scene elements: annotations, walkable area, orbit limits
    const markers = new Markers();
    await scene.add(markers);
    const walkArea = new WalkArea();
    await scene.add(walkArea);
    const walkVoxels = new WalkVoxels();
    await scene.add(walkVoxels);
    const orbitBounds = new OrbitBounds();
    await scene.add(orbitBounds);
    // the tool panels, the markers tool and scene-doc reach these through the scene, as in the fork
    Object.assign(scene as any, { markers, walkArea, orbitBounds });
    new WalkPreview(events, scene);

    // tools
    toolManager.register('markers', new MarkersTool(events, scene, editorUI.canvasContainer));
    toolManager.register('walk', new WalkTool(events, scene, editorUI.canvasContainer));
    toolManager.register('align', new AlignTool(events, scene, editorUI.canvasContainer));

    // dialogs + panels
    const loginDialog = new LoginDialog(events);
    editorUI.topContainer.append(loginDialog);
    events.function('show.loginDialog', () => loginDialog.show());

    const markersPanel = new MarkersPanel(events, scene);
    editorUI.canvasContainer.append(markersPanel);
    events.on('markers.panel.toggle', () => { markersPanel.hidden = !markersPanel.hidden; });
    events.on('markers.panel.show', () => { markersPanel.hidden = false; });
    events.on('markers.panel.hide', () => { markersPanel.hidden = true; });

    const walkPanel = new WalkPanel(events, scene);
    editorUI.canvasContainer.append(walkPanel);
    events.on('walk.panel.toggle', () => { walkPanel.hidden = !walkPanel.hidden; });
    events.on('walk.panel.show', () => { walkPanel.hidden = false; });
    events.on('walk.panel.hide', () => { walkPanel.hidden = true; });

    const alignPanel = new AlignPanel(events, scene);
    editorUI.canvasContainer.append(alignPanel);
    events.on('align.panel.toggle', () => { alignPanel.hidden = !alignPanel.hidden; });
    events.on('align.panel.show', () => { alignPanel.hidden = false; });
    events.on('align.panel.hide', () => { alignPanel.hidden = true; });

    // the tool panels share the Olloport panel's slot next to the toolbar:
    // opening it closes them (it re-opens them through its tool buttons)
    events.on('olloPanel.visible', (visible: boolean) => {
        if (visible) {
            markersPanel.hidden = true;
            walkPanel.hidden = true;
            alignPanel.hidden = true;
        }
    });

    const viewerPreview = new ViewerPreview(events);
    editorUI.canvasContainer.append(viewerPreview);

    // the one panel for scene / media / navigation / tools (right toolbar)
    editorUI.canvasContainer.append(new OlloportPanel(events, editorUI.tooltips));

    // backend
    events.function('ollo.save', () => saveToOlloport(events));
    events.function('ollo.openScene', (sceneId: string) => openSceneFromBackend(events, sceneId));
    events.function('ollo.init', () => initOlloport(events));

    startHelperMonitor(events);
};

export { registerOlloport };
